import { supabase, getAccessToken } from '../lib/supabase';
import { getSupabaseUrl, getSupabaseAnonKey } from '../lib/environments';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface UserReport {
  id: string;
  reporter_id: string;
  reported_user_id: string;
  report_type: 'spam' | 'harassment' | 'inappropriate' | 'impersonation' | 'other';
  report_context: 'dm' | 'invitation' | 'profile' | 'photo' | 'friend_request' | 'other';
  context_id?: string;
  description?: string;
  status: 'pending' | 'reviewed' | 'action_taken' | 'dismissed';
  resolution?: 'dismissed' | 'warned' | 'banned' | null;
  admin_notes?: string | null;
  reviewed_by?: string;
  reviewed_at?: string;
  created_at: string;
  reporter?: {
    id: string;
    full_name: string;
    email: string;
    avatar_url?: string;
  };
  reported_user?: {
    id: string;
    full_name: string;
    email: string;
    avatar_url?: string;
  };
}

export interface ReportCircle {
  id: string;
  name: string;
  owner_id: string;
}

export interface PriorReport {
  id: string;
  report_type: string;
  report_context: string;
  status: string;
  resolution?: string | null;
  created_at: string;
}

export interface ReportCircleBan {
  circle_id: string;
  reason?: string | null;
  created_at: string;
  circle?: { id: string; name: string } | null;
}

export interface ReportDetail {
  report: UserReport;
  /** Circles the reporter and the reported user are both in. */
  shared_circles: ReportCircle[];
  prior_reports: PriorReport[];
  circle_bans: ReportCircleBan[];
}

export interface BlockedUser {
  id: string;
  blocker_id: string;
  blocked_id: string;
  created_at: string;
  blocked_user?: {
    id: string;
    full_name: string;
    email: string;
    avatar_url?: string;
  };
}

export type ReportAction = 'dismiss' | 'warn' | 'ban';

class ModerationApiService {
  private async getAuthHeaders(): Promise<{ Authorization: string } | null> {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) return null;
      return { 'Authorization': `Bearer ${accessToken}` };
    } catch {
      return null;
    }
  }

  private async makeApiCall<T>(
    action: string,
    options: { body?: any; method?: string } = {}
  ): Promise<ApiResponse<T>> {
    try {
      const authHeaders = await this.getAuthHeaders();
      if (!authHeaders) {
        return { success: false, error: 'Authentication required' };
      }

      const method = options.method || 'POST';
      const supabaseUrl = getSupabaseUrl();

      const fetchOptions: RequestInit = {
        method,
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
          'apikey': getSupabaseAnonKey(),
        },
      };

      if (method !== 'GET' && options.body) {
        fetchOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/moderation-api?action=${action}`,
        fetchOptions
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'API call failed' };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async getReports(status?: string): Promise<ApiResponse<{ reports: UserReport[] }>> {
    const action = status ? `list&status=${status}` : 'list';
    return this.makeApiCall(action, { method: 'GET' });
  }

  async getReportDetails(reportId: string): Promise<ApiResponse<ReportDetail>> {
    return this.makeApiCall(`get&report_id=${reportId}`, { method: 'GET' });
  }

  /** A ban is from the chosen circles only, so `circleIds` is required for it. */
  async reviewReport(
    reportId: string,
    action: ReportAction,
    options: { notes?: string; circleIds?: string[] } = {}
  ): Promise<ApiResponse<{ report: UserReport }>> {
    return this.makeApiCall('review', {
      body: { report_id: reportId, action, notes: options.notes, circle_ids: options.circleIds },
    });
  }

  async unbanFromCircle(circleId: string, userId: string): Promise<ApiResponse<{ message: string }>> {
    return this.makeApiCall('unban_from_circle', { body: { circle_id: circleId, user_id: userId } });
  }

  async getUserBlocks(userId: string): Promise<ApiResponse<{ blocks: BlockedUser[] }>> {
    return this.makeApiCall(`user-blocks&user_id=${userId}`, { method: 'GET' });
  }

  async getBlockedByUser(userId: string): Promise<ApiResponse<{ blocked_by: BlockedUser[] }>> {
    return this.makeApiCall(`blocked-by&user_id=${userId}`, { method: 'GET' });
  }
}

export const moderationApi = new ModerationApiService();
